<template>
    <drag-and-drop-endpoints
        :js-plumb-instance="jsPlumbInstance"

        :reference-parameters="referenceParameters"
        :endpoint-parameters="endpoints"

        @endpoints-updated="endpointsUpdated"

        @click="eventProxies.click"
        @dblclick="eventProxies.dblclick"
        @contextmenu="eventProxies.contextmenu"
        @mouseover="eventProxies.mouseover"
        @mouseout="onMouseOut"
        @mousedown="eventProxies.mousedown"
        @mouseup="eventProxies.mouseup"
        @maxConnections="eventProxies.maxConnections"
    >
        <slot />
    </drag-and-drop-endpoints>
</template>

<script>
    import $ from 'jquery';
    
    import journeyBrickEndpointUuidMixin from '../../Mixins/journey-brick-endpoint-uuid'
    import journeyBrickEndpointsMixin from '../../Mixins/journey-brick-endpoints'

    import DragAndDropEndpoints from "../../../../Components/DragAndDrop/DragAndDropEndpoints.vue";
    
    const JS_PLUMB_PAINTSTYLE = 'paintStyle';
    const JS_PLUMB_PARAMETERS = 'parameters';
    
    export default {
        name: 'journey-brick-square-endpoints',
        components: {
            DragAndDropEndpoints
        },
        mixins: [journeyBrickEndpointUuidMixin, journeyBrickEndpointsMixin],
        created() {
            const vm = this;
            vm.referenceParameters.endpoint = ["Dot", { radius: 10 }];
        },
        methods: {
            endpointsUpdated(){
                this.applyEndpointCustomization();
                // vm.$emit('endpoints-updated', $event);
            },
            onMouseOut(endpoint){
                //In some rare cases our tooltip might start lingering
                //thus we need to forcefully hide it to prevent it from becoming a zombie
                if(endpoint.getParameter('hasTooltip')){
                    $(endpoint.canvas).popover('hide');
                }
                
                this.eventProxies.mouseout(...arguments);
            },
            applyEndpointCustomization(){
                const vm = this;
                
                const jsPlumbInstance = vm.jsPlumbInstance;

                jsPlumbInstance.batch(() => {
                    vm.endpoints.filter(e => e.isSource).forEach(endpoint => {
                        const uuid = endpoint.uuid;
                        const {title: tooltipTitle, content: tooltipContent = ''} = endpoint.parameters.tooltip;
                        const { strokeWidth, stroke } = endpoint.paintStyle;
                        
                        const jsPlumbEndpoint = jsPlumbInstance.getEndpoint(uuid);

                        //Our endpoint might've been deleted and thus not exist anymore
                        if(!jsPlumbEndpoint){
                            return;
                        }
                        
                        //Keep track of which endpoints have tooltips
                        //'old' tooltips should always be removed
                        const hadTooltip = jsPlumbEndpoint.getParameter('hasTooltip');
                        jsPlumbEndpoint.setParameter('hasTooltip', endpoint.parameters.hasTooltip);
                        
                        if(hadTooltip){
                            const $canvas = $(jsPlumbEndpoint.canvas);
                            
                            //It does not seem possible to update popover content once the popover has been created
                            //apart from that, the 'destroy' function mentioned in the docs does not seem to exist
                            //thus the best we can do is remove from our data collection, and re-create the popover
                            $canvas.removeData('bs.popover');
                            
                            if(endpoint.parameters.hasTooltip){
                                $canvas.popover({
                                    title: tooltipTitle,
                                    content: tooltipContent,
                                    trigger: 'hover',
                                    html: true
                                });
                            }
                        }
                        
                        //Re-use our old style; we only want to re-color
                        const paintStyle = jsPlumbEndpoint.getPaintStyle();
                        paintStyle.strokeWidth = strokeWidth;
                        paintStyle.stroke = stroke;
                        jsPlumbEndpoint.setPaintStyle(paintStyle);
                    });
                });
            }
        },
        watch: {
            endpoints(newEndpoints, oldEndpoints){
                //This should trigger an event on DragAndDropEndpoints once the new endpoints are processed
                if(newEndpoints.length !== oldEndpoints.length){
                    return;
                }

                const endpointsRequireCustomization = newEndpoints.some(newEndpoint => {
                    const oldEndpoint = oldEndpoints.find(oldEndpoint => oldEndpoint.uuid === newEndpoint.uuid);
                    
                    //This should get picked up and trigger an event by our underlying DragAndDropEndpoints component
                    if(!oldEndpoint){
                        return false;
                    }
                    
                    const diffedProperties = [JS_PLUMB_PAINTSTYLE, JS_PLUMB_PARAMETERS];

                    return diffedProperties.some(property => {
                        const newProperty = newEndpoint[property];
                        const oldProperty = oldEndpoint[property];

                        //When either is falsey, check whether one of two was defined in our previous iteration
                        if(!oldProperty || !newProperty){
                            return newProperty !== oldProperty;
                        }

                        //For the time being we assume a static set of keys
                        //with simply differing values
                        const propertyKeys = Object.keys(newProperty);

                        return propertyKeys.some(key => newProperty[key] !== oldProperty[key]);
                    });
                });

                //Styling has changed, re-apply our customization
                if(endpointsRequireCustomization){
                    this.applyEndpointCustomization();
                }
            }
        },
        computed: {
            endpoints(){
                const vm = this;
                
                const endpoints = [];

                const outgoingEndpoints = vm.outgoingConnections.map(connection => {
                    return  {
                        uuid: vm.getBrickEndpointUuid(connection.id, connection.sourceBrickId),

                        /*
                            Docs regarding endpoint paintStyle options do not match our jsPlumb version
                        
                            From Docs:
                            { 
                                fill:"blue", 
                                outlineStroke:"black", 
                                outlineWidth:1 
                            }
                        
                            From TS declaration:
                                               
                            interface PaintStyle {
                                stroke?: string;
                                fill?: string;
                                strokeWidth?: number;
                            }
                         */
                        [JS_PLUMB_PAINTSTYLE]: { 
                            fill: '#AECAD7',
                            strokeWidth: connection.ui.color ? 3 : undefined,
                            stroke: connection.ui.color || undefined
                        },
                        isSource: true,
                        isTarget: false,
                        [JS_PLUMB_PARAMETERS]: {
                            tooltip: connection.ui.tooltip,
                            hasTooltip: !!(connection.ui.tooltip.title || connection.ui.tooltip.content)
                        }
                    };
                });

                endpoints.push(...outgoingEndpoints);

                if(vm.acceptsIncomingConnections){
                    const incomingEndpoints = vm.incomingConnections.map(connection => ({
                        uuid: vm.getBrickEndpointUuid(connection.id, connection.destinationBrickId),
                        paintStyle: { fill: '#4E7668'},
                        isTarget: true,
                        isSource: false
                    }));

                    //There should always be a single incoming endpoint to accept new connections
                    incomingEndpoints.push({
                        uuid: vm.getBrickEndpointUuid(vm.brickId, vm.brickId),
                        connectorStyle: { stroke: '#BDB6B8', strokeWidth: 6 },
                        paintStyle: { fill: '#4E7668'},
                        isTarget: true,
                        isSource: false
                    });

                    endpoints.push(...incomingEndpoints);
                }
                return endpoints;
            }
        },
        data: () => ({
            referenceParameters: null
        }),
        props: {
            brickId: {
                type: String,
                required: true
            },
            incomingConnections: {
                type: Array,
                required: true
            },
            outgoingConnections: {
                type: Array,
                required: true
            },
            acceptsIncomingConnections : {
                type: Boolean,
                default: true
            }
        }
    }
</script>
